/**
 * TRL + alert configuration constants (moved verbatim from mockData/ — these are
 * definitions used by live pages, not sample data).
 */

// Descriptions are Tim's (Beta-1 feedback D06 §4.2). Since P2-02 the entry form shows FOUR cards:
// Drift + the three TRLs. `TRL_TYPES` keeps the three accumulation types for code that reads
// only those; `CONDITION_TYPES` is the full set the form and every label helper use.
export const TRL_TYPES = [
  { value: 'trap', label: 'Trap', kind: 'trl', description: 'A work environment condition that makes mistakes more likely (e.g., time pressure, distractions/interruptions, etc.)' },
  { value: 'roadblock', label: 'Roadblock', kind: 'trl', description: 'A barrier that makes work harder or take longer to complete' },
  { value: 'landmine', label: 'Landmine', kind: 'trl', description: 'An error, weakness, or lack of clarity in a policy, procedure, process, job instruction, or other documentation' },
];

export const DRIFT_TYPE = {
  value: 'drift',
  label: 'Drift',
  kind: 'drift',
  description: 'Deviations in perception of risk and/or in how work is being performed',
};

/** All four OE Condition types, in the order the entry form shows them (D06: Drift first). */
export const CONDITION_TYPES = [DRIFT_TYPE, ...TRL_TYPES];

/**
 * Tim, 26 Oct (Rewording Items 04–05): the entry form asks in two steps — Drift or Accumulation
 * (the three setups for human error), then, for Accumulation, the "Type of Setup" (Trap /
 * Roadblock / Landmine; Trap then asks for its category). The stored `type` is unchanged.
 */
export const ACCUMULATION_KIND = {
  value: 'trl',
  label: 'Accumulation (Traps, Roadblocks, Landmines)',
  description: 'Process, program, system, and/or structural setups that make mistakes more likely.',
};
export const CONDITION_KINDS = [DRIFT_TYPE, ACCUMULATION_KIND];

/** `kind` of a stored `type` ('drift' | 'trl'); unknown types count as TRLs (legacy records). */
export function conditionKindOf(type) {
  return type === 'drift' ? 'drift' : 'trl';
}

export function conditionTypeLabel(type) {
  return CONDITION_TYPES.find((t) => t.value === type)?.label || String(type || '');
}

/** Drift sub-types (D06; ⚑Q-24 adopted 09-12: D06's two values + "Other (describe)"). */
export const DRIFT_TYPES = [
  { value: 'perception_of_risk', label: 'Drift in Perception of Risk' },
  { value: 'how_work_is_done', label: 'Drift in How Work is Being Done' },
  { value: 'other', label: 'Other (describe)' },
];
export function driftTypeLabel(v) {
  return DRIFT_TYPES.find((d) => d.value === v)?.label || '';
}

/** The 11 predominant traps (TICK A47; keys = WF2/WF4 snake_case, labels = constants.TRAP_NAMES). */
export const TRAP_CATEGORIES = [
  { value: 'time_pressure', label: 'Time Pressure' },
  { value: 'peer_pressure', label: 'Peer Pressure' },
  { value: 'distractions', label: 'Distractions' },
  { value: 'change_off_normal', label: 'Change/Off-Normal Conditions' },
  { value: 'multiple_competing_tasks', label: 'Multiple / Simultaneous / Competing Tasks' },
  { value: 'physical_environment', label: 'Physical Environment' },
  { value: 'overconfidence', label: 'Overconfidence' },
  { value: 'fatigue', label: 'Fatigue' },
  { value: 'vague_conflicting_guidance', label: 'Vague, Conflicting, or Interpretive Guidance' },
  { value: 'first_last_shift', label: 'Lack of focus due to First Shift/Last Shift' },
  { value: 'mental_stress', label: 'Mental Stress' },
];
export function trapCategoryLabel(v) {
  return TRAP_CATEGORIES.find((t) => t.value === v)?.label || '';
}

/** Subtype label for a condition (drift type or trap category), or ''. */
export function conditionSubtypeLabel(c) {
  if (!c) return '';
  if (c.type === 'drift') return driftTypeLabel(c.driftType);
  if (c.type === 'trap') return trapCategoryLabel(c.trapCategory);
  return '';
}

export const SEVERITY_LEVELS = [
  { value: 'I', label: 'Severity I — Low', color: '#2e7d32', description: 'Minor inefficiency or annoyance' },
  { value: 'II', label: 'Severity II — Moderate', color: '#fbc02d', description: 'Slows work or causes occasional errors' },
  { value: 'III', label: 'Severity III — High', color: '#f57c00', description: 'Frequent errors or significant performance hit' },
  { value: 'IV', label: 'Severity IV — Critical', color: '#c62828', description: 'Active threat to safety, reliability, or asset integrity' },
];

// REF = Reverse / Eliminate / Fortify (Tim, D06 §6.5: "Reverse the drift · Eliminate the
// landmine(s) · Fortify associated defense(s)"). Stored key 'reverse' since the P2-20 migration;
// legacy 'reduce' (old docs, n8n WF4 until P7-01) still displays as "Reverse" via refActionLabel().
export const REF_ACTIONS = [
  { value: 'reverse', label: 'Reverse', description: 'Reverse the drift' }, // stored key renamed by the P2-20 migration (scripts/migrate-conditions-beta1.cjs); n8n WF4 still emits 'reduce' until P7-01
  { value: 'eliminate', label: 'Eliminate', description: 'Eliminate the landmine(s)' },
  { value: 'fortify', label: 'Fortify', description: 'Fortify associated defense(s)' },
];

/** Display label for a stored REF action type (handles legacy 'reduce'/'REDUCE' and future 'reverse'). */
export const REF_ACTION_LABELS = { reduce: 'Reverse', reverse: 'Reverse', eliminate: 'Eliminate', fortify: 'Fortify' };
export function refActionLabel(type) {
  const key = String(type || '').toLowerCase();
  return REF_ACTION_LABELS[key] || (type ? String(type) : '');
}

/**
 * Workflow statuses (Tim, D06 §5.1 / D06.1 / D03 §C.3; P2-05). The stored KEYS are the contract
 * with n8n (WF6/WF9), the indexes and the seed scripts — `submitted / under_review / approved /
 * resolved / rejected` never change; P2-05 adds `review_l1`, `resolution_pending`, `withdrawn`,
 * `cancelled`. Labels are what testers see. ⚑Q-08 (adopted 09-12): a closed condition is
 * "Resolved" with the subtitle Remediated (drift) / Eliminated (TRL).
 */
export const CONDITION_STATUS = Object.freeze({
  submitted: 'submitted', // Pending Review — entrant may still edit / withdraw
  review_l1: 'review_l1', // Review in Progress-L1 — the L1 reviewer opened it
  under_review: 'under_review', // Review in Progress-L2 — L1 approved, waiting for L2
  approved: 'approved', // Approved/Active — L2 confirmed; owner works it
  resolution_pending: 'resolution_pending', // Resolution Pending Approval — owner says fixed, L1/L2 sign off
  resolved: 'resolved', // Resolved (Remediated / Eliminated)
  rejected: 'rejected', // Not Approved (L1 or L2 declined)
  withdrawn: 'withdrawn', // entrant withdrew while Pending Review (soft delete, ⚑Q-13)
  cancelled: 'cancelled', // administrator override (P2-36)
});

export const CONDITION_STATUS_LABELS = {
  submitted: 'Pending Review',
  review_l1: 'Review in Progress-L1',
  under_review: 'Review in Progress-L2',
  approved: 'Approved/Active',
  resolution_pending: 'Resolution Pending Approval',
  resolved: 'Resolved',
  rejected: 'Not Approved',
  withdrawn: 'Withdrawn',
  cancelled: 'Cancelled',
};

/** MUI chip colors per status (one place; pages import this instead of their own maps). */
export const CONDITION_STATUS_COLORS = {
  submitted: 'warning',
  review_l1: 'info',
  under_review: 'info',
  approved: 'success',
  resolution_pending: 'secondary',
  resolved: 'success',
  rejected: 'error',
  withdrawn: 'default',
  cancelled: 'default',
};

/** Statuses in workflow order (for sorting/filters). */
export const CONDITION_STATUS_ORDER = Object.freeze([
  'submitted', 'review_l1', 'under_review', 'approved', 'resolution_pending', 'resolved', 'rejected', 'withdrawn', 'cancelled',
]);

/** Still in the review chain (counts as "awaiting" in queues, digests). */
export const AWAITING_STATUSES = Object.freeze(['submitted', 'review_l1', 'under_review', 'resolution_pending']);
/** Open = anything that is not closed; these feed the REPS scores (P2-35) when approved/active. */
export const OPEN_STATUSES = Object.freeze(['submitted', 'review_l1', 'under_review', 'approved', 'resolution_pending']);
export const CLOSED_STATUSES = Object.freeze(['resolved', 'rejected', 'withdrawn', 'cancelled']);
/** Hidden from queues and dashboards, kept in reports (P2-05 / P2-36). */
export const HIDDEN_FROM_QUEUES = Object.freeze(['withdrawn', 'cancelled']);

export function conditionStatusLabel(status) {
  return CONDITION_STATUS_LABELS[status] || String(status || '').replace(/_/g, ' ');
}
export function conditionStatusColor(status) {
  return CONDITION_STATUS_COLORS[status] || 'default';
}
/** "Remediated" for drift, "Eliminated" for TRLs — the closing word Tim's flags use (⚑Q-08). */
export function resolvedSubtitle(condition) {
  if (!condition || condition.status !== 'resolved') return '';
  return conditionKindOf(condition.type) === 'drift' ? 'Remediated' : 'Eliminated';
}
export function isConditionOpen(condition) {
  return Boolean(condition) && OPEN_STATUSES.includes(condition.status);
}

/** The REF Formula exactly as PPI training materials state it (Tim, Beta-1 tickler A13). */
export const REF_FORMULA = 'Reverse Drift. Eliminate Setups. Fortify Defenses.';

export const ALERT_SEVERITY = [
  { value: 'info', label: 'Informational', color: '#0288d1' },
  { value: 'watch', label: 'Watch', color: '#f9a825' },
  { value: 'warning', label: 'Warning', color: '#ef6c00' },
  { value: 'critical', label: 'Critical', color: '#c62828' },
];
